import { FC, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Slide } from "@mui/material";
import cn from "classnames";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { selectFilter, setFiltersByQas } from "../../../store/qas/qasSlice";
import { useGetQuestionsListQuery } from "../../../store/RTKQuery/qasSlice";
import useTranslate from "../../../hooks/useTranslate";
import { useDebounce, useForm } from "../../../hooks";
import useAccessRight from "../../../hooks/useAccessRight";
import { QAS } from "../../../constants/accessRights";
import { QAS_INTERACTIVE, QAS as QAS_PATH } from "../../../constants/routes";
import { DateRange, TextfieldWithAutocomplete } from "../../../ui";
import { FILTERS_LIST } from "./NewQuestionsNavbar.const";
import { INewQuestionsNavbar } from "./NewQuestionsNavbar.props";
import styles from "./NewQuestionsNavbar.module.scss";
import { selectCurrentUserInfo } from "@/store/userSlice";
import { DatasetBlock } from "@/components";
import { selectCategoriesList } from "@/store/qas/qasCategorySlice";
import { Hint } from "@/HOC";
import { selectQasHints } from "@/store/hintsSlice";

const NewQuestionsNavbar: FC<INewQuestionsNavbar> = ({ setQuestionsList, showSidebar }) => {
	const [autocompleteOptions, setAutocompleteOptions] = useState<{ category: string[]; question: string[] }>({ category: [], question: [] });

	const dispatch = useAppDispatch();
	const categoriesList = useAppSelector(selectCategoriesList);
	const filter = useAppSelector(selectFilter); // store - фильтр
	const currentUserInfo = useAppSelector(selectCurrentUserInfo); // store - информация о текущем пользователе
	const prevDataset = useRef(currentUserInfo.dataset); // запоминаем датасет
	const QASHints = useAppSelector(selectQasHints);

	const {
		data: initialData,
		isSuccess: isInitialSuccess,
		isError: isInitialError,
		error: initalError,
		refetch: initialRefetch,
		isUninitialized: isInitialUninitialized,
	} = useGetQuestionsListQuery({ onlyNew: 1 });

	const initialFilters = {
		start: filter.startDate || "",
		end: filter.endDate || "",
		category: filter.category?.name || "",
		question: filter.question || "",
		answer: filter.answer || "",
	};

	const [inputDates, setInputDates] = useState<{ start: string; end: string }>({
		start: initialFilters.start ?? "",
		end: initialFilters.end ?? "",
	});
	const { values, onChange, setValues } = useForm({
		category: initialFilters.category ?? "",
		question: initialFilters.question ?? "",
		answer: initialFilters.answer ?? "",
	});

	// Дебаунсим значения фильтров
	const debouncedFilters = useDebounce(values, 500);
	const shouldRun = !!debouncedFilters;

	const { data, isSuccess, isLoading, isError, error, isUninitialized, refetch } = useGetQuestionsListQuery(
		{
			...debouncedFilters,
			category: values.category === "" ? "" : values.category === "title_notAssigned" ? "-" : categoriesList.data.find((el) => el.name.includes(values.category))?.id, // ищем первый айди который соответствует названию
			created: inputDates.start,
			createdBefore: inputDates.end,
			onlyNew: 1,
			__dataset: currentUserInfo.dataset,
		},
		{
			skip: !shouldRun,
			refetchOnMountOrArgChange: false,
			refetchOnReconnect: false,
		}
	);

	const isStarted = !isUninitialized || !isInitialUninitialized;

	// служебные хуки
	const translate = useTranslate();
	const isAccess = useAccessRight(); // hook для проверки прав доступа
	const navigate = useNavigate(); // hook для навигации

	const delayToHidePage = (link: string): void => {
		setTimeout(() => {
			navigate(link);
		}, 500);
	};

	// Перезапрос за вопросами
	const handleRefetch = () => {
		setQuestionsList((prev) => ({ ...prev, isLoading: true }));

		// этот запрос всегда стартует сам — можно рефетчить
		initialRefetch();

		// второй запрос можно рефетчить только если он реально запущен
		if (isStarted) {
			refetch().finally(() => setQuestionsList((prev) => ({ ...prev, isLoading: false })));
		} else {
			// просто снимаем лоадер, запрос все равно запустится сам позже
			setQuestionsList((prev) => ({ ...prev, isLoading: false }));
		}
	};

	// сбрасываем фильтры при смене набора данных
	useEffect(() => {
		if (prevDataset.current !== currentUserInfo.dataset) {
			// dataset действительно изменился

			setValues({ category: "", answer: "", question: "" });
			setInputDates({ start: "", end: "" });
			if (!isInitialUninitialized) refetch();
		}

		prevDataset.current = currentUserInfo.dataset;
	}, [currentUserInfo.dataset]);

	// Получаем автокомплиты для всех фильтров
	useEffect(() => {
		if (isInitialSuccess) {
			if (categoriesList && initialData.questions) {
				const normalizedData = initialData.questions.reduce<{ category: string[]; question: string[] }>(
					(acc, item) => {
						if (item) {
							acc.question.push(item.question);
							// acc.category.push(item.category ? categoriesList.dictionary[item.category] || translate(item.category) : `-${item.category}`);
						}
						return acc;
					},
					{ category: Object.values(categoriesList.dictionary), question: [] }
				);
				setAutocompleteOptions(normalizedData);
			}
		}

		if (isInitialError) {
			setQuestionsList({ data: { error: 0, questions: [] }, isStarted: isStarted, isLoading: false, isError, refetchNewQuestions: handleRefetch });
		}
	}, [isInitialSuccess, isInitialError, initialData, initalError, currentUserInfo.dataset, JSON.stringify(categoriesList)]);

	useEffect(() => {
		if (isSuccess && data) return setQuestionsList({ data: data, isLoading, isStarted: isStarted, isError, refetchNewQuestions: handleRefetch });
	}, [data, isSuccess, isLoading, isError, error, currentUserInfo.dataset, filter]);

	useEffect(() => {
		const categoryFound =
			values.category === ""
				? null
				: values.category === "-"
				? { id: "", name: "title_notAssigned" }
				: categoriesList.data.find((category) => category.name === values.category);
		// пишем из cookie в store QAS фильтрацию

		dispatch(
			setFiltersByQas({
				category: categoryFound,
				question: values.question,
				answer: values.answer,
				startDate: inputDates.start,
				endDate: inputDates.end,
			})
		);
	}, [debouncedFilters, inputDates]);

	return (
		<Slide direction="right" in={showSidebar} timeout={0} style={{ visibility: "visible", zIndex: 10 }}>
			<div className={cn(styles.navbar)}>
				<div className={cn(styles.sidebar)} style={{ paddingTop: currentUserInfo.dataset === "0" ? "8px" : 0 }}>
					<DatasetBlock />
					<DateRange inputDates={inputDates} setInputDates={setInputDates} />
					{FILTERS_LIST.map((el) =>
						el.name in autocompleteOptions ? (
							<TextfieldWithAutocomplete
								key={el.name}
								autocompleteOptions={autocompleteOptions[el.name as keyof typeof autocompleteOptions]}
								freeSolo={el.name !== "category"}
								value={values[el.name]}
								values={values}
								setValues={setValues}
								onChange={onChange}
								name={el.name}
								label={translate(el.label)}
							/>
						) : (
							<TextfieldWithAutocomplete
								key={el.name}
								value={values.answer}
								freeSolo={el.name !== "category"}
								onChange={onChange}
								name="answer"
								label={translate("title_answer")}
							/>
						)
					)}
				</div>
				{(isAccess(QAS.DOC_LIST) || isAccess(QAS.SUMMARIZE)) && (
					<div className={cn(styles.functionButtonsTabs)}>
						{isAccess(QAS.DOC_LIST) && (
							<p className={cn(styles.functionButtonsTab, styles.functionButtonsTabNonActive)} onClick={() => delayToHidePage(QAS_PATH)}>
								<Hint hint={QASHints.hint_qasDocuments}>
									<>{translate("button_documents")}</>
								</Hint>
							</p>
						)}
						<p className={styles.functionButtonsTab}>
							{" "}
							<Hint hint={QASHints.hint_qasCache}>
								<>{translate("button_cache")}</>
							</Hint>
						</p>
						{isAccess(QAS.SUMMARIZE) && (
							<p className={cn(styles.functionButtonsTab, styles.functionButtonsTabNonActive)} onClick={() => delayToHidePage(QAS_INTERACTIVE)}>
								<Hint hint={QASHints.hint_qasInteractive}>
									<>{translate("button_interactive")}</>
								</Hint>
							</p>
						)}
					</div>
				)}
			</div>
		</Slide>
	);
};

export default NewQuestionsNavbar;
